import React, { useCallback, useEffect, useState } from 'react';
import { BookOpen, Loader2, Plus, RefreshCw, X } from 'lucide-react';
import { apiService } from '../../services/api';

// Lexique du projet : les mots qui reviennent (produits, références, sigles, noms), donnés à la reconnaissance vocale
// des réunions ; les variantes mal entendues sont corrigées dans le direct et les transcripts.

const SOURCE_LABELS = { auto: 'repéré dans le projet', correction: 'appris d\'une correction', muse: 'ajouté par Nexus', equipe: 'ajouté par l\'équipe' };
const SOURCE_TONES = {
  auto: 'border-slate-200 bg-white text-slate-700',
  correction: 'border-amber-200 bg-amber-50 text-amber-900',
  muse: 'border-violet-200 bg-violet-50 text-violet-900',
  equipe: 'border-indigo-200 bg-indigo-50 text-indigo-900',
};
const FIRST_TERMS = 40;

const TermEditor = ({ term, kinds, onSaved, onClose }) => {
  const [variants, setVariants] = useState((term.variants || []).join(', '));
  const [kind, setKind] = useState(term.kind);
  const [error, setError] = useState(null);
  const save = async (status) => {
    try {
      onSaved(await apiService.patch(`/lexicon/${term.id}`, {
        kind, variants: variants.split(',').map((v) => v.trim()).filter(Boolean), ...(status ? { status } : {}),
      }));
    } catch (err) {
      setError(err.message);
    }
  };
  return (
    <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
      <div className="mb-2 flex items-center justify-between">
        <p className="font-semibold text-slate-800">{term.term} <span className="text-xs font-normal text-slate-400">· {SOURCE_LABELS[term.source]}</span></p>
        <button type="button" onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-200"><X size={14} /></button>
      </div>
      <label className="block text-xs text-slate-500">Mal entendu en (séparés par des virgules)
        <input value={variants} onChange={(e) => setVariants(e.target.value)} placeholder="ex. Mouzé, Mousère"
          className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-indigo-100" />
      </label>
      <label className="mt-2 block text-xs text-slate-500">Nature
        <select value={kind} onChange={(e) => setKind(e.target.value)}
          className="ml-2 rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm">
          {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      </label>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={() => save()} className="rounded-lg bg-slate-900 px-3 py-1 text-xs font-semibold text-white hover:bg-slate-700">Enregistrer</button>
        {term.status === 'active' ? (
          <button type="button" onClick={() => save('rejected')} className="rounded-lg px-3 py-1 text-xs text-rose-600 hover:bg-rose-50">Écarter (pas un terme du projet)</button>
        ) : (
          <button type="button" onClick={() => save('active')} className="rounded-lg px-3 py-1 text-xs text-indigo-600 hover:bg-indigo-50">Rétablir</button>
        )}
      </div>
      {error && <p className="mt-2 text-xs text-rose-600">{error}</p>}
    </div>
  );
};

const ProjectLexicon = ({ projectId }) => {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState('');
  const [all, setAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await apiService.get(`/lexicon/projects/${projectId}`));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const add = async (event) => {
    event.preventDefault();
    if (!draft.trim()) return;
    try {
      await apiService.post(`/lexicon/projects/${projectId}`, { term: draft.trim() });
      setDraft('');
      load();
    } catch (err) {
      setError(err.message);
    }
  };
  const refresh = async () => {
    setBusy(true);
    try {
      await apiService.post(`/lexicon/projects/${projectId}/refresh`, {});
      await load();
    } finally {
      setBusy(false);
    }
  };

  const active = (data?.terms || []).filter((t) => t.status === 'active');
  const rejected = (data?.terms || []).filter((t) => t.status !== 'active');
  const order = { equipe: 0, muse: 1, correction: 2, auto: 3 };
  const sorted = [...active].sort((a, b) => order[a.source] - order[b.source] || b.occurrences - a.occurrences);
  const shown = all ? sorted : sorted.slice(0, FIRST_TERMS);
  const editing = data?.terms.find((t) => t.id === selected);

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900"><BookOpen size={16} className="text-slate-400" /> Lexique du projet</h2>
          <p className="text-sm text-slate-500">Donné à la reconnaissance vocale des réunions ; les mots mal entendus connus sont corrigés.</p>
        </div>
        <button type="button" onClick={refresh} disabled={busy} title="Relire le journal, les réunions et les objectifs"
          className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Actualiser
        </button>
      </div>
      {!data ? <p className="text-sm text-slate-400">Chargement…</p> : (
        <>
          <div className="flex flex-wrap gap-1.5">
            {shown.map((term) => (
              <button key={term.id} type="button" onClick={() => setSelected(term.id === selected ? null : term.id)}
                title={`${SOURCE_LABELS[term.source]}${term.variants.length ? ` · mal entendu : ${term.variants.join(', ')}` : ''}`}
                className={`rounded-full border px-2.5 py-0.5 text-xs ${SOURCE_TONES[term.source] || SOURCE_TONES.auto} ${selected === term.id ? 'ring-2 ring-indigo-200' : ''}`}>
                {term.term}{term.variants.length > 0 && <span className="ml-1 text-[10px] opacity-60">↺{term.variants.length}</span>}
              </button>
            ))}
            {sorted.length > FIRST_TERMS && (
              <button type="button" onClick={() => setAll((v) => !v)} className="px-2 text-xs font-medium text-slate-500 hover:text-slate-700">
                {all ? 'moins' : `+ ${sorted.length - FIRST_TERMS}`}
              </button>
            )}
            {!sorted.length && <p className="text-sm text-slate-400">Pas encore de terme repéré.</p>}
          </div>
          {editing && <TermEditor key={editing.id} term={editing} kinds={data.kinds} onClose={() => setSelected(null)}
            onSaved={() => { setSelected(null); load(); }} />}
          <form onSubmit={add} className="mt-3 flex items-center gap-2">
            <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Ajouter un terme (ex. Ceramabond)"
              className="w-64 rounded-lg border border-slate-200 px-2.5 py-1 text-sm outline-none focus:ring-2 focus:ring-indigo-100" />
            <button type="submit" disabled={!draft.trim()} className="rounded-lg bg-slate-900 p-1.5 text-white hover:bg-slate-700 disabled:opacity-40"><Plus size={14} /></button>
          </form>
          {rejected.length > 0 && (
            <details className="mt-2 text-xs text-slate-400">
              <summary className="cursor-pointer">{rejected.length} terme{rejected.length > 1 ? 's' : ''} écarté{rejected.length > 1 ? 's' : ''}</summary>
              <div className="mt-1 flex flex-wrap gap-1">
                {rejected.map((term) => (
                  <button key={term.id} type="button" onClick={() => setSelected(term.id)} className="rounded-full border border-slate-200 px-2 py-0.5 line-through">{term.term}</button>
                ))}
              </div>
            </details>
          )}
          {error && <p className="mt-2 text-xs text-rose-600">{error}</p>}
        </>
      )}
    </div>
  );
};

export default ProjectLexicon;
